import { StateCreator } from 'zustand';

export interface Language {
    name: string;
    icon: string;
    locale: string;
}

export const languages: Language[] = [
    {
        name: 'German',
        icon: 'de',
        locale: 'de-DE'
    },
    {
        name: 'English',
        icon: 'en',
        locale: 'en'
    }
];

const getStoredLanguage = (): Language => {
    try {
        const stored = localStorage.getItem('language');
        if (stored) {
            const parsed = JSON.parse(stored);
            if (parsed && typeof parsed === 'object' && parsed.name && parsed.icon && parsed.locale) {
                return parsed;
            }
        }
    } catch (error) {
        localStorage.removeItem('language');
    }
    return languages[0];
};

export interface SettingsSlice {
    darkMode: boolean;
    selectedLocale: Language;
    cardsTableView: 'cards' | 'table';
    collapseSidebar: boolean;
    sidebarOpen: boolean; // Add this to track sidebar open/closed state
    isMobile: boolean;
    isShowFilterBar: boolean;
    hasMobilePanel: boolean;
    toggleDarkMode: () => void;
    setLocale: (locale: Language) => void;
    toggleCardsTableView: () => void;
    toggleSidebar: () => void;
    setSidebarOpen: (open: boolean) => void; // Add this action
    setIsMobile: (isMobile: boolean) => void;
    setIsShowFilterBar: (isShowFilterBar: boolean) => void;
    setHasMobilePanel: (hasMobilePanel: boolean) => void;
}

export const createSettingsSlice: StateCreator<SettingsSlice> = (set, get) => ({
    darkMode: localStorage.getItem('darkMode') === 'true',
    selectedLocale: getStoredLanguage(),
    cardsTableView: (localStorage.getItem('cardsTableView') as 'cards' | 'table') || 'cards',
    collapseSidebar: false,
    sidebarOpen: localStorage.getItem('sidebarOpen') !== 'false', // Initialize from localStorage, default true
    isMobile: globalThis.window === undefined ? false : globalThis.window.innerWidth <= 480,
    isShowFilterBar: false,
    hasMobilePanel: false,
    toggleDarkMode: () => {
        const newDarkMode = !get().darkMode;
        localStorage.setItem('darkMode', String(newDarkMode));
        set({ darkMode: newDarkMode });
    },

    setLocale: (locale) => {
        localStorage.setItem('language', JSON.stringify(locale));
        set({ selectedLocale: locale });
    },

    toggleCardsTableView: () => {
        const currentView = get().cardsTableView;
        const newView = currentView === 'cards' ? 'table' : 'cards';
        localStorage.setItem('cardsTableView', newView);
        set({ cardsTableView: newView });
    },

    toggleSidebar: () => {
        const newSidebarOpen = !get().sidebarOpen;
        localStorage.setItem('sidebarOpen', String(newSidebarOpen));
        set((state) => ({
            collapseSidebar: !state.collapseSidebar,
            sidebarOpen: newSidebarOpen
        }));
    },

    setSidebarOpen: (open) => {
        localStorage.setItem('sidebarOpen', String(open));
        set({ sidebarOpen: open });
    },

    setIsMobile: (isMobile) => {
        set({ isMobile });
    },

    setIsShowFilterBar: (isShowFilterBar: boolean) => {
        set({ isShowFilterBar });
    },

    setHasMobilePanel: (hasMobilePanel: boolean) => {
        set({ hasMobilePanel });
    }
});
